import { createFileRoute, Link } from "@tanstack/react-router";
import { ServicePageLayout } from "@/components/service/ServicePageLayout";
import { serviceHead } from "@/lib/service-head";
import { loadPageSettings } from "@/lib/page-seo-loader";

const SLUG = "/warehouse-roofing";
const NAME = "Warehouse Roofing";
const TITLE = "Warehouse Roofing Phoenix, AZ | Foam Roof Specialists | Durafoam";
const DESCRIPTION =
  "Phoenix warehouse and distribution center roofing since 1989. Foam (SPF) installs, TPO/mod-bit repair, and recoats for large low-slope industrial buildings across the Valley.";

const faqs = [
  {
    q: "What's the best roofing system for a Phoenix warehouse?",
    a: "For almost every low-slope Phoenix warehouse, we recommend SPF (spray polyurethane foam). Seamless coverage over the many HVAC, drain, and skylight penetrations warehouses carry; R-6.5 per inch of insulation over the entire footprint; the ability to re-slope ponding areas without tear-off; and genuinely renewable through recoats. No membrane system matches all four.",
  },
  {
    q: "Can you install foam over an existing TPO or modified bitumen warehouse roof?",
    a: "Yes — foam-over conversions are one of the most common warehouse projects we run. We clean and prep the existing membrane, remove any wet insulation, and spray directly over the roof. No dumpsters, no tear-off, no exposure of the deck to weather. Much lower disruption than a tear-off replacement.",
  },
  {
    q: "How much does a warehouse roof cost to replace in Phoenix?",
    a: "Full SPF warehouse installs typically run $6–$9 per square foot. TPO tear-off-and-replace runs $7–$11. Recoats on existing foam warehouses run $2.50–$4.50. On a 40,000 sq ft warehouse those spreads are hundreds of thousands of dollars — which is why the repair-recoat-replace decision deserves a written condition report, not a guess.",
  },
  {
    q: "How do you handle ponding water on warehouse roofs?",
    a: "Ponding is common on Phoenix warehouses as buildings settle over decades. Foam is the only commercial roofing system that can be sprayed thicker in low spots to actively re-slope water toward existing drains — no tear-off, no structural work. On chronic ponding roofs we typically fill low spots first, then coat over.",
  },
  {
    q: "Can you re-roof my warehouse while we stay operational?",
    a: "Almost always. We coordinate directly with your logistics team on truck traffic, dock scheduling, and rooftop equipment access. Spray phases get scheduled around production windows and shift changes. Most warehouse jobs run with zero operational downtime.",
  },
  {
    q: "How long does a warehouse foam roof last?",
    a: "A properly installed and maintained SPF warehouse roof in Phoenix routinely lasts 30+ years. Recoat every 5 years and the foam substrate itself is essentially permanent — no more replacement cycles.",
  },
  {
    q: "Do warehouse foam roofs qualify for utility cool-roof rebates?",
    a: "Yes. APS, SRP, and several Phoenix-metro utility programs offer rebates on warehouse cool-roof installs that meet SRI thresholds — and a bright-white elastomeric-coated SPF roof easily qualifies. On a large warehouse footprint the rebate can be substantial. We document the spec at proposal time.",
  },
  {
    q: "Do you handle warehouse roof maintenance contracts?",
    a: "Yes — twice-yearly inspections, drain and scupper cleaning, coating touch-ups, and photo-documented condition reports. Especially valuable for logistics operators who can't afford surprise leaks over inventory.",
  },
];

export const Route = createFileRoute("/warehouse-roofing")({
  loader: () => loadPageSettings(SLUG),
  head: ({ loaderData }) =>
    serviceHead({ override: loaderData?.seo,
      slug: SLUG,
      title: TITLE,
      description: DESCRIPTION,
      serviceName: NAME,
      faqs,
      rating: loaderData?.rating,
      reviewCount: loaderData?.reviewCount,
    }),
  component: Page,
});

function Page() {
  return (
    <ServicePageLayout
      pageSlug="warehouse-roofing"
      eyebrow="Commercial"
      h1="Warehouse Roofing Contractor in Phoenix, AZ"
      subhead="Phoenix warehouse and distribution center roofing specialists since 1989. SPF foam installs, foam-over conversions on aging TPO and modified bitumen, and repair-and-recoat service across the Tempe I-10 industrial corridor, the West Valley, and Sky Harbor logistics parks."
      heroBullets={[
        "SPF foam — our #1 warehouse reroof recommendation",
        "Foam-over conversions on TPO, mod-bit & BUR",
        "Zero-downtime scheduling around dock operations",
        "Cool-roof rebate documentation for utility programs",
      ]}
      whatIs={{
        heading: "Why foam is the right roof for a Phoenix warehouse",
        body: (
          <>
            <p>
              Warehouses are the buildings foam roofing was made for. Large low-slope footprints,
              dense HVAC, forests of drains and scuppers, skylights every few bays, and roof
              traffic from equipment service crews — every single one of those variables is a
              failure point on a membrane system, and a non-issue on foam.
            </p>
            <ul>
              <li>
                <strong>Seamless over every penetration.</strong> HVAC curbs, drains, gas lines,
                conduit stubs, skylights, and equipment supports all self-flash when they're
                covered by foam. On TPO or mod-bit, each one is a hand-detailed flashing that
                has to be inspected annually.
              </li>
              <li>
                <strong>Real insulation across the whole roof.</strong> R-6.5 per inch of SPF —
                two inches puts you around R-13 over the entire warehouse footprint. On a
                Phoenix logistics building running rooftop AC 8+ months a year, that's a
                measurable operating-cost line item.
              </li>
              <li>
                <strong>Ponding? Re-slopable during install.</strong> Warehouses settle. Existing
                drains end up in the wrong spot. Foam can be sprayed thicker in low spots to
                actively re-slope water toward drains — the only commercial roofing system
                that can.
              </li>
              <li>
                <strong>Renewable, not replaceable.</strong> Recoat every 5 years and the
                foam substrate is essentially permanent. Compare to a TPO warehouse roof at
                year 20 facing a full tear-off.
              </li>
            </ul>
          </>
        ),
      }}
      climate={{
        heading: "Warehouse-specific Phoenix considerations",
        body: (
          <>
            <ul>
              <li>
                <strong>Rooftop HVAC dominates the operating budget.</strong> Reflective foam with
                a fresh elastomeric top coat measurably reduces mechanical load. On large
                warehouse footprints, the compounding energy savings often justify the reroof by
                themselves.
              </li>
              <li>
                <strong>Skylight & smoke-hatch flashings fail first.</strong> These are the
                penetrations that leak most often on aging warehouse roofs. Foam wraps them
                continuously.
              </li>
              <li>
                <strong>Dock-door and truck-traffic vibration.</strong> Continuous heavy-vehicle
                movement stresses roof-to-wall connections and parapet caps. We detail parapets
                and expansion joints for the reality of warehouse operations.
              </li>
              <li>
                <strong>Inventory below matters more than tenants.</strong> A warehouse leak isn't
                a tenant-comfort issue — it's an inventory-loss event. Our maintenance program
                is specifically designed to catch problems before they turn into insurance
                claims.
              </li>
              <li>
                <strong>Monsoon microbursts.</strong> Wind-driven debris and sudden pressure
                cycles find every weak flashing. Seamless SPF is the calmest way to handle it.
              </li>
            </ul>
          </>
        ),
      }}
      process={{
        heading: "Our warehouse roofing process",
        steps: [
          {
            title: "Roof survey & operations planning",
            body: "Senior estimator walks the roof, pulls core samples on existing single-ply or BUR to confirm moisture status, and meets with your logistics/facilities team to understand truck flow, dock schedules, and equipment access before we quote.",
          },
          {
            title: "Written proposal, foam-over vs. tear-off",
            body: "Where a foam-over is possible we quote it alongside tear-off pricing so you see the real economics. Coating spec, warranty terms, cool-roof rebate documentation, and a staging plan that respects your operations.",
          },
          {
            title: "Phased install around dock operations",
            body: "Crews stage material in coordination with your loading operations. Spray phases scheduled around production windows. Daily photo updates and end-of-shift housekeeping. Most warehouse jobs run with zero operational downtime.",
          },
          {
            title: "Warranty & recoat schedule",
            body: "Manufacturer warranty plus our workmanship warranty. First recoat inspection scheduled at year 7–10 so the renewable cycle stays on track. Optional maintenance program handles the twice-yearly inspections and drain cleaning warehouses genuinely need.",
          },
        ],
      }}
      whyChoose={{
        heading: "Related commercial services for warehouse operators",
        body: (
          <>
            <ul>
              <li>
                Aging TPO or mod-bit warehouse roof that needs a plan? See{" "}
                <Link
                  to="/commercial-foam-roofing"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  commercial foam roofing
                </Link>{" "}
                for our foam-over conversion process.
              </li>
              <li>
                Warehouse roof with coating end-of-life but sound substrate?{" "}
                <Link
                  to="/commercial-roof-coating"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  Commercial roof coating
                </Link>{" "}
                — 10–15 years of new life at 20–30% of replacement cost.
              </li>
              <li>
                Active warehouse leak or targeted repair scope? See{" "}
                <Link
                  to="/commercial-roof-repair"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  commercial roof repair
                </Link>{" "}
                or{" "}
                <Link
                  to="/emergency-roof-leak-repair"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  emergency roof leak repair
                </Link>
                .
              </li>
              <li>
                Full commercial services overview:{" "}
                <Link
                  to="/commercial-roofing"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  commercial roofing
                </Link>
                . Real Phoenix numbers:{" "}
                <Link
                  to="/commercial-roofing-cost-phoenix"
                  className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
                >
                  commercial roofing cost
                </Link>
                . Cost the blocker? <Link to="/financing" className="font-semibold text-primary underline underline-offset-2 hover:text-accent">Ask us about payment options</Link>.
              </li>
            </ul>
          </>
        ),
      }}
      faqs={faqs}
      ctaTitle="Warehouse foam reroof, foam-over conversion, or targeted repair?"
      ctaBody="Call us or request a free warehouse inspection. We'll walk the roof, pull core samples where needed, and hand you a written recommendation with foam-over vs. tear-off pricing side by side — plus a staging plan that respects your dock operations."
    />
  );
}
